import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Button,
  Anchor,
  Timeline,
  Grid,
} from "@mantine/core";
import {
  IconMail,
  IconBrandLinkedin,
  IconDownload,
  IconExternalLink,
  IconBriefcase,
  IconSchool,
} from "@tabler/icons-react";

export function ResumePage() {
  const coreCompetencies = [
    "Engineering Leadership",
    "Transition Leadership",
    "Strategic Planning",
    "Process Optimization",
    "Team Scaling",
    "Agile Transformation",
    "Jira Cloud Administration",
  ];

  return (
    <Stack gap="xl">
      {/* Header */}
      <Card
        p="xl"
        withBorder
        style={{
          backgroundColor: "rgba(18, 18, 26, 0.8)",
          borderColor: "rgba(139, 92, 246, 0.4)",
        }}
      >
        <Stack gap="md">
          <Title order={1} c="white">
            BRIAN G. JONES
          </Title>
          <Text size="lg" c="gray.2" fw={700} style={{ fontSize: "1.25rem" }}>
            Software Engineering Executive & Strategic Leader
          </Text>
          <Group gap="xl">
            <Group gap="xs">
              <IconBrandLinkedin size={16} color="white" />
              <Anchor
                href="https://www.linkedin.com/in/brian-g-jones/"
                target="_blank"
                rel="noopener noreferrer"
                c="white"
                size="sm"
                td="none"
              >
                LinkedIn
              </Anchor>
            </Group>
            <Group gap="xs">
              <IconMail size={16} color="white" />
              <Anchor href="/contact" c="white" size="sm" td="none">
                Contact Me
              </Anchor>
            </Group>
          </Group>
        </Stack>
      </Card>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="xl">
            {/* Executive Summary */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="md">
                <Title order={2} style={{ color: "#8b5cf6" }}>
                  Executive Summary
                </Title>
                <Text style={{ color: "#d1d5db" }}>
                  Strategic software engineering executive with 25+ years of
                  progressive technology leadership experience. Currently
                  leading 60+ engineers building comprehensive tourism
                  technology platform including CMS, CRM, analytics, mobile
                  apps, and digital marketing solutions. Proven track record of
                  successfully navigating multiple acquisitions and
                  organizational transformations, including AOL-Time Warner
                  merger and Simpleview&apos;s acquisition by Granicus. Expert
                  in scaling engineering organizations while maintaining
                  operational excellence across diverse enterprise solutions.
                </Text>
              </Stack>
            </Card>

            {/* Career Timeline */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="md">
                <Group gap="xs">
                  <IconBriefcase size={24} color="#8b5cf6" />
                  <Title order={2} style={{ color: "#8b5cf6" }}>
                    Career Highlights
                  </Title>
                </Group>

                <Timeline active={0} bulletSize={24} lineWidth={2}>
                  <Timeline.Item
                    bullet={<IconBriefcase size={12} />}
                    title="Director of Engineering"
                  >
                    <Text size="sm" style={{ color: "#a1a1aa" }}>
                      Simpleview (acquired by Granicus) • 2018–Present
                    </Text>
                    <Text size="sm" mt="xs" style={{ color: "#d1d5db" }}>
                      Engineering leader overseeing 60+ software and QA
                      engineers building comprehensive tourism technology
                      platform including CMS, CRM, Data Analytics, Event
                      Management, Mobile Apps, and Digital Marketing tools.
                      Successfully navigated Simpleview&apos;s acquisition by
                      Granicus, ensuring seamless integration and team retention
                      while scaling engineering operations.
                    </Text>
                    <Group gap="md" mt="sm">
                      <Badge size="sm" variant="light" color="green">
                        60+ Team Members
                      </Badge>
                      <Badge size="sm" variant="light" color="teal">
                        Acquisition Leadership
                      </Badge>
                      <Badge size="sm" variant="light" color="blue">
                        Full Product Suite
                      </Badge>
                    </Group>
                  </Timeline.Item>

                  <Timeline.Item
                    bullet={<IconBriefcase size={12} />}
                    title="Manager, Project Management Office"
                  >
                    <Text size="sm" style={{ color: "#a1a1aa" }}>
                      Amadeus Revenue Integrity • 2010–2018
                    </Text>
                    <Text size="sm" mt="xs" style={{ color: "#d1d5db" }}>
                      Built and managed PMO supporting 150+ domestic and
                      international airline customers implementing Amadeus
                      Revenue Integrity platform - sophisticated revenue
                      optimization system preventing revenue leakage,
                      eliminating fraudulent bookings, and optimizing inventory
                      management. Led 40+ staff through 3 direct reports,
                      implementing tracking systems and developing 50+ KPI
                      reports for executive decision-making.
                    </Text>
                    <Group gap="md" mt="sm">
                      <Badge size="sm" variant="light" color="blue">
                        150+ Global Airlines
                      </Badge>
                      <Badge size="sm" variant="light" color="green">
                        40+ Staff
                      </Badge>
                      <Badge size="sm" variant="light" color="orange">
                        Revenue Optimization
                      </Badge>
                    </Group>
                  </Timeline.Item>

                  <Timeline.Item
                    bullet={<IconBriefcase size={12} />}
                    title="Project Manager"
                  >
                    <Text size="sm" style={{ color: "#a1a1aa" }}>
                      KCC/Coplink • 2008–2010
                    </Text>
                    <Text size="sm" mt="xs" style={{ color: "#d1d5db" }}>
                      Managed complex ETL migration projects for 50+ law
                      enforcement agencies implementing COPLINK - advanced
                      &quot;Google for cops&quot; platform integrating vast
                      police databases for crime solving. Coordinated full
                      deployment lifecycle from contract review to system
                      implementation, enabling cross-jurisdictional information
                      sharing, tactical lead generation, and advanced analytics.
                    </Text>
                    <Group gap="md" mt="sm">
                      <Badge size="sm" variant="light" color="red">
                        50+ Agencies
                      </Badge>
                      <Badge size="sm" variant="light" color="violet">
                        Advanced Analytics
                      </Badge>
                      <Badge size="sm" variant="light" color="cyan">
                        Data Integration
                      </Badge>
                    </Group>
                  </Timeline.Item>

                  <Timeline.Item
                    bullet={<IconBriefcase size={12} />}
                    title="Technical Manager"
                  >
                    <Text size="sm" style={{ color: "#a1a1aa" }}>
                      AOL Time Warner • 2006–2008
                    </Text>
                    <Text size="sm" mt="xs" style={{ color: "#d1d5db" }}>
                      Managed AOL Beta production portal development with up to
                      11 direct reports and 30 supervised staff. Led teams
                      through AOL-Time Warner acquisition, maintaining
                      operational continuity during organizational
                      transformation. Oversaw international coordination, SDLC
                      implementation, and QA lab operations.
                    </Text>
                  </Timeline.Item>

                  <Timeline.Item
                    bullet={<IconBriefcase size={12} />}
                    title="Senior QA Engineer / Associate Technical Manager"
                  >
                    <Text size="sm" style={{ color: "#a1a1aa" }}>
                      AOL • 1999–2006
                    </Text>
                    <Text size="sm" mt="xs" style={{ color: "#d1d5db" }}>
                      Led QA engineering initiatives and tool consolidation
                      projects. Authored functional requirements, established
                      quality standards, and consulted on technical feasibility
                      across multiple AOL product lines including AIM,
                      CompuServe, and Netscape.
                    </Text>
                  </Timeline.Item>
                </Timeline>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="sm">
            {/* Core Competencies */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="sm">
                <Title order={3} style={{ color: "#8b5cf6" }}>
                  Core Competencies
                </Title>
                <Group gap="xs">
                  {coreCompetencies.map((competency) => (
                    <Badge
                      key={competency}
                      size="sm"
                      variant="filled"
                      style={{ backgroundColor: "#B97F24", color: "white" }}
                    >
                      {competency}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </Card>

            {/* Education */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="sm">
                <Group gap="xs">
                  <IconSchool size={20} color="#8b5cf6" />
                  <Title order={3} style={{ color: "#8b5cf6" }}>
                    Education
                  </Title>
                </Group>
                <Stack gap="xs">
                  <Text fw={500} size="sm" style={{ color: "#ffffff" }}>
                    University of Phoenix
                  </Text>
                  <Text size="sm" style={{ color: "#a1a1aa" }}>
                    Information Technology
                  </Text>
                </Stack>
              </Stack>
            </Card>

            {/* Certifications */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="sm">
                <Title order={3} style={{ color: "#8b5cf6" }}>
                  Certifications
                </Title>
                <Group gap="xs">
                  <Badge
                    variant="outline"
                    size="sm"
                    style={{ borderColor: "#B97F24", color: "#B97F24" }}
                  >
                    Certified Agile Leader
                  </Badge>
                  <Badge
                    variant="outline"
                    size="sm"
                    style={{ borderColor: "#B97F24", color: "#B97F24" }}
                  >
                    Certified ScrumMaster
                  </Badge>
                  <Badge
                    variant="outline"
                    size="sm"
                    style={{ borderColor: "#B97F24", color: "#B97F24" }}
                  >
                    Certified Scrum Product Owner
                  </Badge>
                </Group>
              </Stack>
            </Card>

            {/* Quantified Impact */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="md">
                <Title order={3} style={{ color: "#8b5cf6" }}>
                  Quantified Impact
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap="xs" align="center">
                      <Text size="xl" fw={700} style={{ color: "#8b5cf6" }}>
                        60+
                      </Text>
                      <Text size="sm" ta="center" style={{ color: "#a1a1aa" }}>
                        Engineering Team Members
                      </Text>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="xs" align="center">
                      <Text size="xl" fw={700} style={{ color: "#8b5cf6" }}>
                        150+
                      </Text>
                      <Text size="sm" ta="center" style={{ color: "#a1a1aa" }}>
                        Global Airlines Served
                      </Text>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="xs" align="center">
                      <Text size="xl" fw={700} style={{ color: "#8b5cf6" }}>
                        25+
                      </Text>
                      <Text size="sm" ta="center" style={{ color: "#a1a1aa" }}>
                        Years Technology Leadership
                      </Text>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="xs" align="center">
                      <Text size="xl" fw={700} style={{ color: "#8b5cf6" }}>
                        4+
                      </Text>
                      <Text size="sm" ta="center" style={{ color: "#a1a1aa" }}>
                        Market Verticals Led
                      </Text>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>

            {/* Market Verticals */}
            <Card
              withBorder
              style={{
                backgroundColor: "rgba(18, 18, 26, 0.8)",
                borderColor: "rgba(139, 92, 246, 0.4)",
                /* backdropFilter: 'blur(disabled)' - DISABLED */
              }}
            >
              <Stack gap="sm">
                <Title order={3} style={{ color: "#8b5cf6" }}>
                  Market Verticals
                </Title>
                <Stack gap="xs">
                  <Text size="sm" style={{ color: "#d1d5db" }}>
                    • Consumer Internet & Media
                  </Text>
                  <Text size="sm" style={{ color: "#d1d5db" }}>
                    • Travel & Hospitality Tech
                  </Text>
                  <Text size="sm" style={{ color: "#d1d5db" }}>
                    • Law Enforcement / Public Safety
                  </Text>
                  <Text size="sm" style={{ color: "#d1d5db" }}>
                    • Government / Civic Tech
                  </Text>
                  <Text size="sm" style={{ color: "#d1d5db" }}>
                    • Enterprise SaaS / B2B Software
                  </Text>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Call to Action */}
      <Card
        p="xl"
        withBorder
        style={{
          backgroundColor: "rgba(18, 18, 26, 0.8)",
          borderColor: "rgba(139, 92, 246, 0.4)",
        }}
      >
        <Stack gap="md" align="center">
          <Title order={3} c="white" ta="center">
            Ready to Drive Engineering Excellence Together?
          </Title>
          <Text c="gray.2" ta="center" maw={700}>
            This overview highlights key achievements and leadership approach.
            For comprehensive work history, detailed project outcomes, and
            technical specifications.
          </Text>
          <Group gap="md" justify="center">
            <Button
              variant="filled"
              color="violet"
              leftSection={<IconDownload size={16} />}
              size="md"
            >
              Download Full Resume
            </Button>
            <Button
              variant="outline"
              color="violet"
              leftSection={<IconExternalLink size={16} />}
              component="a"
              href="https://www.linkedin.com/in/brian-g-jones/"
              target="_blank"
              rel="noopener noreferrer"
              size="md"
            >
              View LinkedIn Profile
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
